import type { CompetencyAssignmentJSON, RoleCompetenciesResponseJSON } from "@performyard/service-contracts";

// Every row a SuperRow-enabled table displays carries a stable `_rowId` (whatever field that surface
// treats as its natural id — assignment `_id`, role-competency `competency_id`, etc.). The synthetic
// detail row inserted on expand reuses the parent's data, marked `superRow: true` and pointing back at
// the row it belongs to via `parentRowId` (its own `_rowId` is suffixed to stay unique).
export type SuperRowRow<TRow> = TRow & {
	_rowId: string;
	superRow?: true;
	parentRowId?: string;
};

// The assignment-flavored row used by the Competencies pages' own tables (Team/Company/My Competencies).
export type CompetencyAssignmentRow = SuperRowRow<CompetencyAssignmentJSON>;

// The role-interior flavor — the endpoint already joins competency name/description/levels onto each row.
export type RoleCompetencyDetail = RoleCompetenciesResponseJSON["competencies"][number];

// Mutable object threaded through ag-grid's `context`. The caret + full-width content renderers are
// plain functions ag-grid re-invokes via applyTransaction/refreshCells — they hold no state of their
// own, so this is how they read the latest expansion state and reach the toggle handlers.
export type SuperRowContext = {
	expandedRowId: string | null;
	onExpandToggle: (rowId: string) => void;
	isLevelExpanded: (rowId: string, rank: number) => boolean;
	onToggleLevel: (rowId: string, rank: number) => void;
	isDescriptionExpanded: (rowId: string) => boolean;
	onToggleDescription: (rowId: string) => void;
};

// The py-components Table wrapper seeds `loading=true` with empty-object rows; `competency_id` is
// present on every real row across every SuperRow surface, so it doubles as the loading check.
export const isLoadingRow = (data: { competency_id?: string } | undefined): boolean => !data?.competency_id;
